/**
 * ImportanceCard — one classified change (F7 下钻面板).
 *
 * Collapsed: importance badge + change-type chip + node-deleted marker + a short
 * excerpt of the changed text. Expanded (drill-down): 判定依据 (rationale) → 变更
 * 前后文 (excerptV1 → excerptV2 from nodeRef). Self-contained from the GET payload
 * + nodeRef; never needs to anchor into the diff DOM (importance node ids come
 * from the materialized working tree, which the diff views don't share — F6-6
 * parity).
 */
import { useState } from 'react';

import type { ImportanceResult } from '../../lib/veridoc-api';

import { changeTypeLabel, normalizeImportance } from './constants';
import { ImportanceBadge } from './ImportanceBadge';

/** Best available one-line excerpt for the collapsed header. */
function headerExcerpt(result: ImportanceResult): string {
  const ref = result.nodeRef;
  const text = (ref?.excerptV2 || ref?.excerptV1 || '').trim();
  return text || '（无文本摘录）';
}

export function ImportanceCard({
  result,
}: {
  result: ImportanceResult;
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const importance = normalizeImportance(result.payload.importance);
  const typeLabel = changeTypeLabel(result.payload.changeType);
  const rationale = (result.payload.rationale ?? '').trim();
  const ref = result.nodeRef;

  return (
    <li className="rounded-lg border border-ink-200 bg-ink-0 shadow-sm">
      <div className="flex flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <ImportanceBadge importance={importance} />
              <span className="inline-flex items-center rounded-sm bg-ink-100 px-1.5 py-0.5 text-xs text-ink-600">
                {typeLabel}
              </span>
              {result.nodeDeleted && (
                <span className="inline-flex items-center rounded-sm bg-ink-100 px-1.5 py-0.5 text-xs text-ink-500">
                  节点已删除
                </span>
              )}
            </div>
            <p className="truncate font-serif text-sm text-ink-800">
              {headerExcerpt(result)}
            </p>
          </div>

          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="inline-flex h-btn-sm shrink-0 items-center gap-1 rounded-sm border border-ink-200 bg-ink-50 px-2 text-xs text-ink-600 hover:bg-ink-100 hover:text-ink-900"
          >
            <span
              aria-hidden="true"
              className={`transition-transform duration-fast ${expanded ? 'rotate-90' : ''}`}
            >
              ›
            </span>
            {expanded ? '收起' : '下钻详情'}
          </button>
        </div>

        {expanded && (
          <div className="flex flex-col gap-3 border-t border-ink-100 pt-3">
            <Field label="判定依据">
              <p className="text-sm leading-relaxed text-ink-700">
                {rationale || '（无依据说明）'}
              </p>
            </Field>

            <Field label="变更前">
              <ExcerptBlock text={ref?.excerptV1 ?? null} emptyHint="（新增，变更前无内容）" />
            </Field>

            <Field label="变更后">
              <ExcerptBlock text={ref?.excerptV2 ?? null} emptyHint="（删除，变更后无内容）" />
            </Field>
          </div>
        )}
      </div>
    </li>
  );
}

function ExcerptBlock({
  text,
  emptyHint,
}: {
  text: string | null;
  emptyHint: string;
}): React.JSX.Element {
  const trimmed = (text ?? '').trim();
  if (!trimmed) {
    return <p className="text-sm text-ink-400">{emptyHint}</p>;
  }
  return (
    <blockquote className="rounded-md border-l-2 border-ink-300 bg-ink-50 px-3 py-2 font-serif text-sm leading-relaxed text-ink-700">
      {trimmed}
    </blockquote>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-ink-400">
        {label}
      </span>
      {children}
    </div>
  );
}

export default ImportanceCard;
